import React, { useState, useRef, useEffect } from 'react';
import LoyaltyCard from './LoyaltyCard';
import LoyaltyDesigner from './LoyaltyDesigner';
import SlidePanel from '../shared/SlidePanel';
import { loadScheme, saveScheme, deleteScheme, getSchemeInflight, getSchemeStats, blankScheme, currentOwnerId, loadSharing, saveSharing, teamForSharing, hydrateLoyalty, hydrateElections, applySchemeToAllLocations } from './loyaltyStore';
import useSites from '../multiLocation/useSites';
import { RewardUnlockedView } from '../wallet/StampEarnedCelebration';
import '../wallet/StampEarnedCelebration.css';
import '../shared/ui.css';
import '../marketing/campaigns/Campaigns.css';

/**
 * Marketing › Loyalty (content)
 * One scheme per business: stamps (buy X get Y free) or points per pound,
 * with a salon-branded loyalty card the client actually sees. All editing
 * (rules and design alike) happens inside LoyaltyDesigner; this page is
 * the summary and the live card.
 */

const ink = '#1A1A1E';
const muted = '#8E8E93';
const line = '#ECECF0';

const card = { background: '#fff', border: `1px solid ${line}`, borderRadius: 14, padding: 24 };

// App-first: the phone gets its own layout (card-as-hero, stats as a
// swipeable row) rather than the desktop cards squeezed narrow.
const useIsMobile = () => {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const on = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return m;
};

// Overflow-menu row, matching the automation detail panel's .crp-menu-item.
const cardMenuItem = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
  padding: '10px 12px', border: 'none', background: 'none', borderRadius: 8,
  fontFamily: 'inherit', fontSize: 14, color: '#29282D', cursor: 'pointer',
};

export default function LoyaltyContent({ salonName }) {
  let displayName = salonName;
  try {
    const u = JSON.parse(localStorage.getItem('user')) || {};
    if (u.employmentTypeId === 2) {
      displayName = u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || salonName;
    }
  } catch (e) { /* salon name fallback */ }
  salonName = displayName;
  const ownerId = currentOwnerId();
  const [scheme, setScheme] = useState(() => loadScheme(ownerId));
  const [editorScheme, setEditorScheme] = useState(null); // scheme being edited/created in the designer
  const [previewFill, setPreviewFill] = useState(null); // animated stamp demo
  const [viewOpen, setViewOpen] = useState(false); // the scheme's view panel
  const [cardMenuOpen, setCardMenuOpen] = useState(false); // overflow menu in that panel
  const [stats, setStats] = useState(null); // salon-wide stats (loyalty/stats); null until loaded
  const [sharing, setSharing] = useState(() => loadSharing(currentOwnerId()));
  const isMobile = useIsMobile();
  let isSelfEmployed = false;
  try { isSelfEmployed = (JSON.parse(localStorage.getItem('user')) || {}).employmentTypeId === 2; } catch (e) { /* owner default */ }
  const setShare = (patch) => setSharing(saveSharing(currentOwnerId(), patch));
  // Hydrate from the backend on mount (localStorage is only the cache now).
  useEffect(() => {
    let alive = true;
    hydrateLoyalty(ownerId).then(({ scheme: fresh }) => {
      if (alive) setScheme(fresh);
      if (fresh) getSchemeStats().then((s) => { if (alive) setStats(s); });
      const hydrateShare = isSelfEmployed ? Promise.resolve() : hydrateElections(ownerId);
      return hydrateShare;
    }).then(() => { if (alive) setSharing(loadSharing(currentOwnerId())); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [teamOpen, setTeamOpen] = useState(false);
  // Roster comes from the election hydrate (cached on `sharing`); the helper
  // is the pre-hydrate fallback so the panel is never momentarily empty.
  const team = sharing.team || teamForSharing(ownerId);
  const per = sharing.perStylist || {};
  const setStylist = (id, patch) => setShare({ perStylist: { ...per, [id]: { offered: false, joined: false, ...(per[id] || {}), ...patch } } });
  const optedCount = team.filter((t) => (per[t.id] || {}).offered && (per[t.id] || {}).joined).length;
  const sharingCard = (
    <div style={{ ...card, padding: '18px 24px', marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, color: ink, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {isSelfEmployed ? 'Let the salon manage loyalty for you' : 'Salon managed loyalty'}
            {!isSelfEmployed && team.length ? (
              <span style={{ fontSize: 11, fontWeight: 700, color: muted, background: '#F2F2F7', borderRadius: 100, padding: '3px 10px' }}>
                {optedCount} of {team.length} opted in
              </span>
            ) : null}
          </h2>
          <p style={{ fontSize: 12.5, color: muted, lineHeight: 1.55, margin: 0 }}>
            {isSelfEmployed
              ? 'Join your salon\u2019s scheme instead of running your own. It only takes effect once the salon has offered it, the same double opt-in as marketing, and you can leave at any time.'
              : 'Offer your scheme stylist by stylist; each then chooses whether to join, the same double opt-in as marketing. Until they do, their own schemes stay entirely theirs.'}
          </p>
        </div>
        {!isSelfEmployed ? (
          <button type="button" onClick={() => setTeamOpen(true)} style={{ padding: '9px 16px', border: `1px solid ${line}`, borderRadius: 10, background: '#fff', fontSize: 13, fontWeight: 600, color: ink, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}>
            Manage stylists
          </button>
        ) : null}
        {isSelfEmployed ? (
        <button
          type="button"
          role="switch"
          aria-checked={sharing.joinSalonScheme}
          onClick={() => (isSelfEmployed
            ? setShare({ joinSalonScheme: !sharing.joinSalonScheme })
            : setShare({ offerToSelfEmployed: !sharing.offerToSelfEmployed }))}
          style={{
            width: 44, height: 26, borderRadius: 100, border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0,
            background: (isSelfEmployed ? sharing.joinSalonScheme : sharing.offerToSelfEmployed) ? '#007EA0' : '#DEDEE2', transition: 'background 0.15s'
          }}
        >
          <span style={{
            display: 'block', width: 22, height: 22, borderRadius: '50%', background: '#fff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'transform 0.15s',
            transform: sharing.joinSalonScheme ? 'translateX(18px)' : 'translateX(0)'
          }} />
        </button>
        ) : null}
      </div>
      {teamOpen ? (
        <SlidePanel title="Salon managed loyalty" width={420} onClose={() => setTeamOpen(false)}>
          <div className="sp-form">
            <p className="sp-hint" style={{ marginTop: 0 }}>
              Toggle who you offer the scheme to. Each stylist accepts from their own account, and joining only happens when both sides agree.
            </p>
            {team.length ? team.map((t) => {
              const row = per[t.id] || {};
              return (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #F2F2F7' }}>
                  <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: ink }}>{t.name}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: row.joined ? '#007EA0' : muted, background: row.joined ? 'rgba(0,126,160,0.1)' : '#F2F2F7', borderRadius: 100, padding: '3px 9px' }}>
                    {row.joined ? 'Opted in' : 'Not opted in'}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!row.offered}
                    aria-label={`Offer to ${t.name}`}
                    onClick={() => setStylist(t.id, { offered: !row.offered })}
                    style={{ width: 44, height: 26, borderRadius: 100, border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0, background: row.offered ? '#007EA0' : '#DEDEE2', transition: 'background 0.15s' }}
                  >
                    <span style={{ display: 'block', width: 22, height: 22, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'transform 0.15s', transform: row.offered ? 'translateX(18px)' : 'translateX(0)' }} />
                  </button>
                </div>
              );
            }) : (
              <p className="sp-hint">No self-employed stylists found on this account yet. Your team appears here as soon as they do.</p>
            )}
          </div>
        </SlidePanel>
      ) : null}
      {(isSelfEmployed ? sharing.joinSalonScheme : sharing.offerToSelfEmployed) ? (
        <p style={{ fontSize: 11.5, fontWeight: 600, color: '#6B6B73', margin: '10px 0 0' }}>
          {isSelfEmployed
            ? 'Saved. Once the salon offers it too, your clients earn on the salon’s card from their next visit.'
            : 'Saved. Each stylist who joins from their own account has their clients earn on your card from their next visit.'}
        </p>
      ) : null}
    </div>
  );
  const [popIndex, setPopIndex] = useState(-1);
  const [rewardPreview, setRewardPreview] = useState(false); // client's reward screen
  const previewTimer = useRef(null);
  const rewardTimer = useRef(null);

  const isCreate = !!editorScheme && !editorScheme.id;

  const openCreate = (mode) => setEditorScheme(blankScheme(mode));
  const openEdit = () => setEditorScheme({ ...scheme, design: { ...scheme.design } });

  const handleDesignerSave = (saved) => {
    // A brand-new scheme goes live immediately; editing keeps its status.
    const status = scheme ? scheme.status : 'live';
    // A new scheme's stats weren't loaded (there was no scheme on mount), so
    // the panel said "reporting connecting" until a refresh. Load them once
    // the server has the scheme.
    const stored = saveScheme(ownerId, { ...saved, status }, {
      onSynced: () => getSchemeStats().then((s) => { if (s) setStats(s); }),
    });
    setScheme(stored);
    setEditorScheme(null);
  };

  // Delete flow: open a confirmation with the in-flight reward counts so the
  // salon decides whether to honour or void what clients have already earned.
  const [deleteModal, setDeleteModal] = useState(null); // null | { loading, rewardsReady, clientsWithRewards, clientsInProgress }
  const openDeleteModal = () => {
    setDeleteModal({ loading: true, rewardsReady: 0, clientsWithRewards: 0, clientsInProgress: 0 });
    getSchemeInflight().then((s) => setDeleteModal({ loading: false, ...s }));
  };
  const confirmDelete = (honour) => {
    deleteScheme(ownerId, honour);
    setDeleteModal(null);
    setScheme(null);
  };

  // Multi-location: each location runs its own scheme. The owner can copy
  // this one to their other locations in one go.
  const { multi: multiSite, canAddLocation: isBusinessOwner } = useSites();
  const [applyAll, setApplyAll] = useState({ busy: false, note: '' });
  const applyToAll = () => {
    if (!scheme || applyAll.busy) return;
    setApplyAll({ busy: true, note: '' });
    applySchemeToAllLocations(ownerId, scheme)
      .then((r) => {
        const n = (r && Array.isArray(r.appliedTo)) ? r.appliedTo.length : 0;
        setApplyAll({ busy: false, note: n ? `Now running at ${n} other location${n === 1 ? '' : 's'} too.` : 'There are no other locations of yours to copy it to.' });
      })
      .catch((e) => {
        const d = e && e.response && e.response.data;
        const msg = (d && (d.message || (d.error && (d.error.responseMessage || d.error.message)))) || 'That didn’t work just now. Please try again.';
        setApplyAll({ busy: false, note: msg });
      });
  };

  const toggleLive = () => {
    const stored = saveScheme(ownerId, { ...scheme, status: scheme.status === 'live' ? 'paused' : 'live' });
    setScheme(stored);
  };

  // The delight moment: simulate a visit and watch the stamp land. Filling
  // the last slot then plays the client's own "Reward unlocked" screen, so
  // the salon sees the whole payoff, not just the stamps.
  const previewVisit = () => {
    if (!scheme || scheme.mode !== 'stamps') return;
    const required = scheme.stampsRequired || 6;
    const base = Math.ceil(required / 2);
    const next = previewFill == null ? base : (previewFill >= required ? base : previewFill + 1);
    setPreviewFill(next);
    setPopIndex(next - 1);
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => setPopIndex(-1), 650);
    clearTimeout(rewardTimer.current);
    if (next >= required) {
      // Let the final stamp land first, exactly as the client experiences it.
      rewardTimer.current = setTimeout(() => setRewardPreview(true), 700);
    }
  };

  // Timers outlive a fast unmount (closing the panel mid-animation) otherwise.
  useEffect(() => () => {
    clearTimeout(previewTimer.current);
    clearTimeout(rewardTimer.current);
  }, []);

  // Shared by the summary on the page and the full list in the view panel.
  const rewardSummary = scheme && (scheme.mode === 'stamps'
    ? `${scheme.rewardLabel} after ${scheme.stampsRequired} stamps`
    : `${scheme.pointsLabel} at ${(scheme.pointsTarget || 0).toLocaleString()} points`);
  const statusLine = scheme && (scheme.status === 'live'
    ? `Live now: every completed, paid visit earns ${scheme.mode === 'stamps' ? 'a stamp' : 'points'}, and the card is in your clients’ wallets.`
    : 'Paused: clients cannot see the card and nothing is earned.');
  const schemeRules = scheme ? [
    { k: 'How clients earn', v: scheme.mode === 'stamps' ? 'One stamp per completed, paid visit' : `${scheme.pointsPerPound} point${scheme.pointsPerPound > 1 ? 's' : ''} per £1 spent` },
    { k: 'The reward', v: rewardSummary },
    { k: 'Built-in rules', v: 'Earned on completed and paid visits only. Tips, deposits and gift cards never earn.' },
  ] : [];

  const designer = editorScheme ? (
    <LoyaltyDesigner
      scheme={editorScheme}
      salonName={salonName}
      isCreate={isCreate}
      onClose={() => setEditorScheme(null)}
      onSave={handleDesignerSave}
    />
  ) : null;

  /* ── No scheme yet: pick a mode, then build it all in the designer ── */
  if (!scheme) {
    return (
      <div className="cmp-wrap" style={{ maxWidth: 900 }}>
        <div className="cmp-head">
          <div className="cmp-head-text">
            <h1 className="cmp-title">Loyalty</h1>
            <p className="cmp-subtitle">Reward the clients who keep coming back, with a card worth showing off.</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 14 }}>
          {[
            {
              mode: 'stamps',
              title: 'Stamp card',
              desc: 'Every visit earns a stamp. Collect the card, get a treat. Simple, visual and loved by regulars.',
              eg: 'e.g. 6 visits, the 7th blow dry is free',
            },
            {
              mode: 'points',
              title: 'Points on spend',
              desc: 'Every pound earns points towards rewards you choose. Flexible for salons with varied prices.',
              eg: 'e.g. 1 point per £1, free treatment at 500',
            },
          ].map((opt) => (
            <button
              key={opt.mode}
              type="button"
              onClick={() => openCreate(opt.mode)}
              style={{ ...card, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit', transition: 'border-color 0.15s, box-shadow 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#007EA0'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,126,160,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = line; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <span style={{ display: 'block', fontSize: 17, fontWeight: 700, color: ink, marginBottom: 6 }}>{opt.title}</span>
              <span style={{ display: 'block', fontSize: 13, color: muted, lineHeight: 1.55, marginBottom: 10 }}>{opt.desc}</span>
              <span style={{ display: 'inline-block', fontSize: 11.5, fontWeight: 700, color: '#007EA0', background: 'rgba(0,126,160,0.08)', borderRadius: 100, padding: '4px 11px' }}>{opt.eg}</span>
            </button>
          ))}
        </div>

        {sharingCard}

        {designer}
      </div>
    );
  }

  /* ── Scheme exists: summary + live card ── */
  const required = scheme.stampsRequired || 6;
  const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : (n || '0'));
  const statTiles = [
    { k: 'Active cards', v: stats ? fmt(stats.activeCards) : '0' },
    { k: scheme.mode === 'stamps' ? 'Stamps this month' : 'Points this month', v: stats ? fmt(stats.earnedThisMonth) : '0' },
    { k: 'Rewards redeemed', v: stats ? fmt(stats.rewardsRedeemed) : '0' },
    { k: 'Visits from members', v: stats ? fmt(stats.memberVisits) : '0' },
  ];

  const statusControls = scheme.status === 'live' ? (
    <>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#007EA0', background: 'rgba(0,126,160,0.12)', borderRadius: 100, padding: '8px 15px' }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#007EA0', boxShadow: '0 0 0 3px rgba(0,126,160,0.22)' }} />
        Live
      </span>
      <button type="button" onClick={toggleLive} style={{ padding: '9px 16px', border: `1px solid ${line}`, borderRadius: 10, background: '#fff', fontSize: 13, fontWeight: 600, color: '#6B7280', cursor: 'pointer', fontFamily: 'inherit' }}>
        Pause scheme
      </button>
      {multiSite && isBusinessOwner ? (
        <button type="button" onClick={applyToAll} disabled={applyAll.busy} title="Copies this scheme to your other locations. Each location keeps its own cards." style={{ padding: '9px 16px', border: `1px solid ${line}`, borderRadius: 10, background: '#fff', fontSize: 13, fontWeight: 600, color: '#1A1A1E', cursor: applyAll.busy ? 'default' : 'pointer', fontFamily: 'inherit' }}>
          {applyAll.busy ? 'Applying…' : 'Use at all my locations'}
        </button>
      ) : null}
      {applyAll.note ? <span style={{ fontSize: 12.5, color: '#6B7280' }}>{applyAll.note}</span> : null}
    </>
  ) : (
    <>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#A05A1F', background: '#FCEFE0', borderRadius: 100, padding: '8px 15px' }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#E08A2B' }} />
        Paused
      </span>
      <button type="button" onClick={toggleLive} style={{ padding: '11px 22px', border: 'none', borderRadius: 10, background: '#007EA0', fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
        Go live
      </button>
    </>
  );
  const subtitleText = scheme.mode === 'stamps'
    ? `Every completed visit earns a stamp. ${required} stamps, ${(scheme.rewardLabel || 'a treat').toLowerCase()}.`
    : `Every pound spent earns ${scheme.pointsPerPound || 1} point${(scheme.pointsPerPound || 1) > 1 ? 's' : ''}, with ${(scheme.pointsLabel || 'a reward').toLowerCase()} at ${(scheme.pointsTarget || 500).toLocaleString()}.`;

  return (
    <div className="cmp-wrap" style={{ maxWidth: 980 }}>
      {/* On the phone the card leads and this whole block moves below it */}
      {!isMobile && (
      <div className="cmp-head">
        <div className="cmp-head-text">
          <h1 className="cmp-title">Loyalty</h1>
          <p className="cmp-subtitle">{subtitleText}</p>
        </div>
        <div className="cmp-head-actions" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {statusControls}
        </div>
      </div>
      )}

      {isMobile ? (
        <>
          {/* The card IS the product, so the phone opens on it — sitting
              directly on the page like a card in a wallet. A dark tray
              behind it read as a card inside a black card, so there is no
              tray at all, just a soft lift. Tapping it opens the scheme. */}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setViewOpen(true)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewOpen(true); } }}
            style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px', marginBottom: 14, cursor: 'pointer' }}
          >
            <div style={{ borderRadius: 18, boxShadow: '0 2px 6px rgba(26,26,30,0.08), 0 14px 32px rgba(26,26,30,0.18)' }}>
              <LoyaltyCard
                design={scheme.design}
                scheme={scheme}
                state={{ filled: previewFill != null ? previewFill : undefined }}
                salonName={salonName}
                width={scheme.design?.orientation === 'portrait' ? 230 : Math.min(340, (typeof window !== 'undefined' ? window.innerWidth : 375) - 32)}
                animateIndex={popIndex}
              />
            </div>
          </div>

          {/* The demo sits under the card, not on it, so the stage stays
              card-shaped. */}
          {scheme.mode === 'stamps' && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <button
                type="button"
                onClick={previewVisit}
                style={{ padding: '9px 20px', border: '1px solid rgba(0,126,160,0.35)', borderRadius: 100, background: '#fff', fontSize: 13, fontWeight: 700, color: '#007EA0', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Preview a visit
              </button>
            </div>
          )}

          {/* The card's info, below it: what the scheme does and its status */}
          <p style={{ fontSize: 13.5, color: muted, lineHeight: 1.55, margin: '0 0 12px' }}>{subtitleText}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            {statusControls}
          </div>

          {/* The main action on the page, so it wears the app's primary
              button rather than hiding as a ghost row below the stats */}
          <button
            type="button"
            className="beu-btn-pill"
            onClick={() => setViewOpen(true)}
            style={{ width: '100%', marginBottom: 20 }}
          >
            View scheme
          </button>

          {/* Stats as one swipeable, edge-bleeding row — the same treatment
              the campaigns page gives its numbers on the phone. */}
          <div className="cmp-kpis" style={{ marginBottom: 4 }}>
            {statTiles.map((st) => (
              <div key={st.k} className="cmp-kpi">
                <p className="cmp-kpi-label">{st.k}</p>
                <p className="cmp-kpi-num">{st.v}</p>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 11.5, color: muted, margin: '0 0 14px' }}>
            {stats
              ? 'Live figures, updated as clients earn and redeem.'
              : 'Salon-wide totals switch on as soon as reporting is connected.'}
          </p>
        </>
      ) : (
        <>
      {/* Performance: live figures from the scheme. */}
      <div style={{ ...card, padding: '18px 24px', marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: ink, margin: '0 0 12px' }}>How it is doing</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: 12 }}>
          {statTiles.map((st) => (
            <div key={st.k} style={{ background: '#F7F7F9', borderRadius: 10, padding: '12px 14px' }}>
              <p style={{ fontSize: 11.5, color: muted, margin: '0 0 4px' }}>{st.k}</p>
              <p style={{ fontSize: 22, fontWeight: 700, color: ink, margin: 0, letterSpacing: '-0.3px' }}>{st.v}</p>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11.5, color: muted, margin: '12px 0 0' }}>
          {stats
            ? 'Live figures from your scheme, updated as clients earn and redeem.'
            : 'Clients are already earning on their cards; these salon-wide totals switch on as soon as reporting is connected.'}
        </p>
      </div>

      {/* Summary only. The full rules, and the edit and delete actions, live
          in the view panel, the same way an automation opens from its card. */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #F2F2F7' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: ink, margin: 0 }}>Your loyalty card</h2>
        </div>

        <div
          role="button"
          tabIndex={0}
          onClick={() => setViewOpen(true)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewOpen(true); } }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 28, padding: 24, alignItems: 'center', cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <LoyaltyCard
              design={scheme.design}
              scheme={scheme}
              state={{ filled: previewFill != null ? previewFill : undefined }}
              salonName={salonName}
              width={scheme.design?.orientation === 'portrait' ? 240 : 340}
              animateIndex={popIndex}
            />
            {/* Minor action, so it sits quietly under the thing it demonstrates.
                Stops the click so it does not also open the panel. */}
            {scheme.mode === 'stamps' && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); previewVisit(); }}
                style={{ padding: '4px 6px', border: 'none', background: 'none', fontSize: 12, fontWeight: 600, color: '#007EA0', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Preview a visit
              </button>
            )}
          </div>

          <div>
            <p style={{ fontSize: 12, color: muted, margin: '0 0 3px' }}>The reward</p>
            <p style={{ fontSize: 15, fontWeight: 600, color: ink, margin: 0, lineHeight: 1.45 }}>{rewardSummary}</p>
            <p style={{ fontSize: 11.5, color: muted, margin: '12px 0 0', lineHeight: 1.55 }}>{statusLine}</p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', padding: '14px 24px', borderTop: '1px solid #F2F2F7', background: '#FBFBFC' }}>
          <p style={{ flex: 1, minWidth: 200, fontSize: 12, color: muted, margin: 0, lineHeight: 1.5 }}>
            Open the scheme to see every rule, edit the card or delete it.
          </p>
          <button
            type="button"
            onClick={() => setViewOpen(true)}
            style={{ padding: '10px 18px', border: 'none', borderRadius: 10, background: '#29282D', fontSize: 13, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
          >
            View scheme
          </button>
        </div>
      </div>
        </>
      )}

      {sharingCard}

      {/* View mode: the whole scheme read-only, with edit and delete behind
          the three dots and the frequent action pinned in the footer. Mirrors
          the custom automation detail panel. */}
      {viewOpen && scheme && (
        <SlidePanel
          title="Your loyalty card"
          width={480}
          onClose={() => { setCardMenuOpen(false); setViewOpen(false); }}
          headerRight={
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setCardMenuOpen((o) => !o)}
                aria-label="Loyalty scheme actions"
                aria-haspopup="menu"
                aria-expanded={cardMenuOpen}
                style={{ width: 36, height: 36, borderRadius: 8, background: '#F3F4F6', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: ink, flexShrink: 0 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
              </button>
              {cardMenuOpen && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 9 }} onClick={() => setCardMenuOpen(false)} />
                  <div role="menu" style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, background: '#fff', border: '1px solid #F0F0F0', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', minWidth: 200, zIndex: 10, padding: 4 }}>
                    <button type="button" role="menuitem" onClick={() => { setCardMenuOpen(false); setViewOpen(false); openEdit(); }} style={cardMenuItem}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                      Edit loyalty scheme
                    </button>
                    <button type="button" role="menuitem" onClick={() => { setCardMenuOpen(false); setViewOpen(false); openDeleteModal(); }} style={{ ...cardMenuItem, color: '#dc2626' }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                      Delete scheme
                    </button>
                  </div>
                </>
              )}
            </div>
          }
          footer={
            <button
              type="button"
              onClick={() => { setViewOpen(false); openEdit(); }}
              className="sp-btn-primary"
              style={{ width: '100%' }}
            >
              Edit loyalty scheme
            </button>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 24 }}>
            <LoyaltyCard
              design={scheme.design}
              scheme={scheme}
              state={{ filled: previewFill != null ? previewFill : undefined }}
              salonName={salonName}
              width={scheme.design?.orientation === 'portrait' ? 220 : 320}
              animateIndex={popIndex}
            />
            {scheme.mode === 'stamps' && (
              <button
                type="button"
                onClick={previewVisit}
                style={{ padding: '4px 6px', border: 'none', background: 'none', fontSize: 12, fontWeight: 600, color: '#007EA0', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Preview a visit
              </button>
            )}
          </div>

          {schemeRules.map((row, i) => (
            <div key={row.k} style={{ padding: '11px 0', borderTop: i ? '1px solid #F2F2F7' : 'none' }}>
              <p style={{ fontSize: 12, color: muted, margin: '0 0 3px' }}>{row.k}</p>
              <p style={{ fontSize: 14, fontWeight: 600, color: ink, margin: 0, lineHeight: 1.45 }}>{row.v}</p>
            </div>
          ))}

          <div style={{ padding: '11px 0', borderTop: '1px solid #F2F2F7' }}>
            <p style={{ fontSize: 12, color: muted, margin: '0 0 3px' }}>Status</p>
            <p style={{ fontSize: 14, fontWeight: 600, color: ink, margin: 0, lineHeight: 1.45 }}>
              {scheme.status === 'live' ? 'Live' : 'Paused'}
            </p>
          </div>

          <p style={{ fontSize: 11.5, color: muted, margin: '14px 0 0', lineHeight: 1.55 }}>{statusLine}</p>
        </SlidePanel>
      )}

      {designer}

      {deleteModal && (
        <SlidePanel
          title="Delete this loyalty scheme?"
          width={440}
          onClose={() => setDeleteModal(null)}
          footer={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
              {deleteModal.clientsWithRewards > 0 && (
                <button
                  type="button"
                  onClick={() => confirmDelete(true)}
                  disabled={deleteModal.loading}
                  style={{ width: '100%', height: 44, borderRadius: 10, border: 'none', background: '#29282D', color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: deleteModal.loading ? 'default' : 'pointer', opacity: deleteModal.loading ? 0.5 : 1 }}
                >
                  Honour earned rewards &amp; delete
                </button>
              )}
              <button
                type="button"
                onClick={() => confirmDelete(false)}
                disabled={deleteModal.loading}
                style={{ width: '100%', height: 44, borderRadius: 10, border: 'none', background: '#B14438', color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: deleteModal.loading ? 'default' : 'pointer', opacity: deleteModal.loading ? 0.5 : 1 }}
              >
                {deleteModal.clientsWithRewards > 0 ? 'Remove for everyone & delete' : 'Delete scheme'}
              </button>
              <button
                type="button"
                onClick={() => setDeleteModal(null)}
                style={{ width: '100%', height: 44, borderRadius: 10, border: '1px solid #e5e5e5', background: '#fff', color: '#6b7280', fontSize: 14, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          }
        >
          {deleteModal.loading ? (
            <p className="sp-hint" style={{ margin: 0 }}>Checking what clients have earned…</p>
          ) : deleteModal.clientsWithRewards > 0 ? (
            <p style={{ fontSize: 14, color: ink, margin: 0, lineHeight: 1.55 }}>
              <strong>{deleteModal.clientsWithRewards}</strong> client{deleteModal.clientsWithRewards === 1 ? ' has' : 's have'} a reward ready to redeem
              {deleteModal.rewardsReady > deleteModal.clientsWithRewards ? ` (${deleteModal.rewardsReady} in total)` : ''}
              {deleteModal.clientsInProgress > 0 ? <>, and <strong>{deleteModal.clientsInProgress}</strong> more {deleteModal.clientsInProgress === 1 ? 'has' : 'have'} stamps in progress</> : ''}.
              {' '}Keep those rewards redeemable, or clear everything?
            </p>
          ) : deleteModal.clientsInProgress > 0 ? (
            <p style={{ fontSize: 14, color: ink, margin: 0, lineHeight: 1.55 }}>
              <strong>{deleteModal.clientsInProgress}</strong> client{deleteModal.clientsInProgress === 1 ? ' has' : 's have'} stamps in progress (no rewards ready yet). Deleting clears their progress, and this cannot be undone.
            </p>
          ) : (
            <p className="sp-hint" style={{ margin: 0 }}>No clients have rewards or stamps in progress. This cannot be undone.</p>
          )}
        </SlidePanel>
      )}

      {/* The payoff of Preview a visit: the exact full-screen moment the
          client gets when their last stamp lands, rendered from this salon's
          own card and reward. Closing rewinds the preview so it can run again. */}
      {rewardPreview && scheme && (
        <RewardUnlockedView
          zIndex={100020} /* above the view SlidePanel (99999), which the client's own 10001 is not */
          design={scheme.design}
          scheme={scheme}
          state={{ filled: scheme.stampsRequired || 6, rewardsAvailable: 1 }}
          salonName={salonName}
          rewardName={scheme.rewardLabel || 'your reward'}
          subline={`Your card is full at ${salonName}. Show this on your next visit to claim it.`}
          caption="This is what your client sees the moment their card fills."
          actions={(
            <button
              type="button"
              className="reward-full-btn"
              onClick={() => { setRewardPreview(false); setPreviewFill(null); setPopIndex(-1); }}
            >
              Close preview
            </button>
          )}
        />
      )}
    </div>
  );
}
